import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../LoadingPanel";
import { addNotificationWithTimeout } from '../../app/addNotificationWithTimeout';
import { handleResponse } from '../../app/handleResponse';
import { getCSRFToken } from '../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Form, Field, FormElement } from "@progress/kendo-react-form";

import {
    FormAutoComplete,
} from "../forms/form-components";

import './report/Report.scss';

export const AdministratorVenueShortingStatuses = () => {
    const dispatch = useDispatch();

    const formReference = useRef(null);

    const [venueShortingStatuses, setVenueShortingStatuses] = useState([]);
    const [venueShortingStatusCount, setVenueShortingStatusCount] = useState(0);
    const [skipCount, setSkipCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);
    const [pageNumber, setPageNumber] = useState(1);
    const [areVenueShortingStatusesLoading, setAreVenueShortingStatusesLoading] = useState(true);
    const [searchCriteria, setSearchCriteria] = useState(undefined);
    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(undefined);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);

    const autocompleteTimeout = useRef();
    const timeoutDelay = 500; //Milliseconds

    const getData = (dataItem) => {        
        dataItem.venueKey = undefined;
        if (venues.length > 0) {
            const venue = venues.find(element => element.currentName === venueName);

            if (venue) {
                dataItem.venueKey = venue.venueKey;
            }
        }

        setSearchCriteria(dataItem);
        setPageNumber(1);
        getVenueShortingStatuses(dataItem, 1);
    }

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }

        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, timeoutDelay);

        setAreVenuesLoading(true);
    };

    const getVenueShortingStatuses = (dataItem, newPageNumber) => {
        setAreVenueShortingStatusesLoading(true);

        if (!dataItem) {
            dataItem = searchCriteria;
        }

        if (!dataItem) {
            dataItem = {};
        }

        dataItem.pageNumber = newPageNumber;
    
        fetch(`/api/administrator/venueshortingstatuses`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setAreVenueShortingStatusesLoading(false);                
                setItemsPerPage(result.itemsPerPage);
                setSkipCount(result.itemsPerPage * (result.pageNumber - 1));
                setVenueShortingStatusCount(result.totalCount);                
                setVenueShortingStatuses(result.statuses);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueShortingStatusesLoading(false);
            });
    }

    const updateShortingStatus = (venueKey, isShorting) => {
        const dataItem = {
            venueKey: venueKey,
            isShorting: isShorting,
        };
    
        fetch(`/api/administrator/venueshortingstatus`,
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Updated venue shorting status");     

                getVenueShortingStatuses(null, pageNumber);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const pageChange = (event) => {
        const newPageNumber = (event.page.skip / event.page.take) + 1;
        setPageNumber(newPageNumber);
        getVenueShortingStatuses(null, newPageNumber);
    };

    const UpdateStatusCell = (props) => {  
        const venueKey = props.dataItem.venueKey;
        const isShorting = props.dataItem.isShorting;
        
        return (
            <td>
                <Button 
                    type={"button"} 
                    onClick={() => updateShortingStatus(venueKey, !isShorting)}
                    themeColor={isShorting ? undefined : "primary"}
                    icon={isShorting ? "close" : "plus"}>
                    {isShorting ? "Stop Shorting" : "Start Shorting"}
                </Button>
            </td>
        );
    };

    useEffect(() => {
        document.title = "Venue Shorting Statuses";

        getVenueShortingStatuses(null, 1);
    }, []);

    return (
        <div className="administratorReport">
            <Form
                onSubmit={getData}
                ignoreModified={true}
                ref={formReference}
                render={(formRenderProps) => (
                    <FormElement>
                        <fieldset className={"k-form-fieldset container"}>
                            <div className="row">                                
                                <div className="col-md-10">
                                    <Field
                                        id={"venueName"}
                                        name={"venueName"}
                                        label={"Venue"}
                                        component={FormAutoComplete}
                                        data={venues}
                                        placeholder="e.g. Roseland"
                                        textField="currentName"
                                        onChange={venueChanged}
                                        loading={areVenuesLoading}
                                    />
                                </div>                                
                                <div className="col-md-2">
                                    <div className="k-form-field">
                                        <label className="k-label">&nbsp;</label>
                                        <Button
                                            type={"submit"}
                                            themeColor={"primary"}
                                            icon="search"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </FormElement>
                )}
            />

            {areVenueShortingStatusesLoading ? <LoadingPanel /> : <></>}
        
            <Grid
                data={venueShortingStatuses}
                resizable={true}
                skip={skipCount}
                take={itemsPerPage}
                total={venueShortingStatusCount}
                pageable={true}
                onPageChange={pageChange}
                scrollable="none"
            >
                <Column field="venueName" title={"Name"} />
                <Column field="venueCity" title={"City"} />
                <Column field="venueRegion" title={"Region"} />
                <Column field="venueCountryCode" title={"Country"} />
                <Column field="isShorting" title={"Shorting"} />                      
                <Column cell={UpdateStatusCell} />
            </Grid>
        </div>
    );
}